import { ZfIcon, ZfLoadingSpinner, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router';
import { useEvents } from '../../api';
import { DEFAULT_INPUT_DATE_FORMAT } from '../../config/constants';
import type { RoadmapEvent } from '../../interfaces/event';
import { isMaturity, isRoadmapEventUpcoming } from '../../utils/eventUtils';
import { List } from '../atoms';

export const UpcomingEventList: FunctionComponent = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const { data: allEvents } = useEvents();
    const upcomingEvents: RoadmapEvent[] = allEvents
        ? [...allEvents.lifeEvents, ...allEvents.advisoryNeedTasks, ...allEvents.maturities]
              .filter(isRoadmapEventUpcoming)
              .sort((a, b) => moment(a.dueDate).valueOf() - moment(b.dueDate).valueOf())
        : [];

    const handleClick = (item: RoadmapEvent) => {
        const eventId = isMaturity(item) ? (item.qualifiedKey ?? '') : (item.key ?? item.id?.toString() ?? '');

        navigate({
            pathname: '../roadmap',
            search: createSearchParams({ eventId }).toString(),
        });
    };
    const handleGoToClientRoadmapClick = () => navigate('../roadmap');

    return (
        <Suspense fallback={<ZfLoadingSpinner />}>
            <ZfStack direction="column" spacing="medium" style={{ flex: 1, width: '100%', overflow: 'hidden', padding: 'var(--padding-vertical-md)' }}>
                <List
                    title={t('upcoming')}
                    titleSpacing="medium"
                    containerStyle={{ flex: '1 1 0', minHeight: 0 }}
                    items={upcomingEvents.map((event) => ({
                        title: event?.topicName?.[i18n.language] ?? '',
                        description: moment(event?.dueDate).format(DEFAULT_INPUT_DATE_FORMAT),
                        data: event,
                    }))}
                    getItemKey={(event, index) => event.key ?? index}
                    hasBorderBottom={(index) => index < upcomingEvents.length - 1}
                    style={{ flex: '1 1 0', minHeight: 0, overflow: 'auto' }}
                    onItemClick={handleClick}
                    renderTrailing={(event) => (
                        <ZfStack slot="trailing" direction="row" spacing="extra-extra-large">
                            <div style={{ width: '25px' }} />
                            <ZfTypography variant="body-small" alignment="right">
                                {t(`eventStatus-${event.status}`)}
                            </ZfTypography>
                            <ZfIcon name="chevron_right" size="medium" aria-hidden="true" />
                        </ZfStack>
                    )}
                />
                <ZfTextButton className="ml-auto" size="medium" hierarchy="secondary" onClick={handleGoToClientRoadmapClick}>
                    {t('viewClientRoadmap')}
                </ZfTextButton>
            </ZfStack>
        </Suspense>
    );
};
